/**
 * Persistent store of accepted delete-for-everyone tombstones.
 *
 * Tombstones are monotonic: once a `(messageId)` is recorded as deleted,
 * later writes are no-ops — there is no "undelete." This is by design.
 * Replaying the original message after the tombstone has landed must NOT
 * resurrect the plaintext on the receiver's screen; the channel/UI layer
 * consults `isDeleted(messageId)` before rendering.
 *
 * Hardening rule:
 *   `record(tombstone, requestedBy)` rejects any request where the
 *   authenticated envelope sender (`requestedBy`) does not match the
 *   tombstone's `originalAuthorId`. Only the original message author
 *   can delete-for-everyone. The envelope signature already binds
 *   `requestedBy` to the holder of that user's identity key.
 *
 * The store is intentionally tolerant of out-of-order delivery: a
 * tombstone can land *before* the original message body — the message
 * will simply render as a tombstone the moment it does arrive.
 *
 * Storage: IndexedDB via the `idb-keyval` helpers (same dependency used
 * by `SignalProtocolStore`). Each tombstone is stored as a JSON blob under
 * the key `tomb:<sha256hex(messageId)>` in an isolated `tombstone-store`
 * database. SHA256 of the messageId (not the messageId itself) is used as
 * the key to bound key length and avoid IndexedDB key encoding edge cases.
 *
 * Mirrors iOS `TombstoneStore.swift`.
 */

import { get, set, del, clear as idbClear, createStore } from "idb-keyval";
import type { Tombstone } from "./Tombstone";

// ---------------------------------------------------------------------------
// Value types
// ---------------------------------------------------------------------------

export interface TombstoneRecord {
  readonly messageId: string;
  readonly originalAuthorId: string;
  /** Peer-claimed time of deletion (Unix seconds). */
  readonly claimedTs: number;
  /** Local time (ISO 8601 string) when we recorded this tombstone. */
  readonly observedAt: string;
}

// ---------------------------------------------------------------------------
// Error types
// ---------------------------------------------------------------------------

/**
 * Thrown by `record` when the authenticated envelope sender does not match
 * `tombstone.originalAuthorId`. Fail-closed: the store persists nothing.
 */
export class TombstoneAuthorMismatchError extends Error {
  readonly claimedAuthor: string;
  readonly requestedBy: string;

  constructor(claimedAuthor: string, requestedBy: string) {
    super(
      `tombstone author mismatch: claimedAuthor=${claimedAuthor} requestedBy=${requestedBy}`,
    );
    this.name = "TombstoneAuthorMismatchError";
    this.claimedAuthor = claimedAuthor;
    this.requestedBy = requestedBy;
  }
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

/** Default IndexedDB database name for the singleton store. */
const DEFAULT_DB_NAME = "kaether-tombstone-store";
const DEFAULT_STORE_NAME = "tombstones";

/**
 * TombstoneStore: IndexedDB-backed, monotonic, idempotent per-message
 * tombstone state. Mirrors iOS `TombstoneStore` semantics.
 */
export class TombstoneStore {
  private readonly idb: ReturnType<typeof createStore>;

  constructor(dbName = DEFAULT_DB_NAME) {
    this.idb = createStore(dbName, DEFAULT_STORE_NAME);
  }

  // -------------------------------------------------------------------------
  // Recording
  // -------------------------------------------------------------------------

  /**
   * Records a tombstone observation.
   *
   * @param tombstone  Parsed from the decrypted control message.
   * @param requestedBy  Envelope's authenticated sender userId.
   * @param observedAt  Local clock at observation time (defaults to now).
   *
   * @returns The resulting `TombstoneRecord` — either newly written or the
   *   existing one if this tombstone was already recorded (idempotent).
   *
   * @throws `TombstoneAuthorMismatchError` when `requestedBy` does not match
   *   `tombstone.originalAuthorId`. The store persists nothing in this case.
   *
   * Mirrors iOS `TombstoneStore.record(tombstone:requestedBy:at:)`.
   */
  async record(
    tombstone: Tombstone,
    requestedBy: string,
    observedAt: Date = new Date(),
  ): Promise<TombstoneRecord> {
    // CRITICAL: Fail-closed author check. Only the original sender can delete.
    if (tombstone.originalAuthorId !== requestedBy) {
      throw new TombstoneAuthorMismatchError(
        tombstone.originalAuthorId,
        requestedBy,
      );
    }

    const key = await idbKey(tombstone.messageId);

    // Monotonic / idempotent: if already deleted, return the existing record.
    const existing = await get<TombstoneRecord>(key, this.idb);
    if (existing !== undefined) {
      return existing;
    }

    const record: TombstoneRecord = {
      messageId: tombstone.messageId,
      originalAuthorId: tombstone.originalAuthorId,
      claimedTs: tombstone.ts,
      observedAt: observedAt.toISOString(),
    };
    await set(key, record, this.idb);
    return record;
  }

  // -------------------------------------------------------------------------
  // Queries
  // -------------------------------------------------------------------------

  /** Returns true if a tombstone has been recorded for the given messageId. */
  async isDeleted(messageId: string): Promise<boolean> {
    const key = await idbKey(messageId);
    const record = await get<TombstoneRecord>(key, this.idb);
    return record !== undefined;
  }

  /** Returns the tombstone record for a messageId, or undefined if not deleted. */
  async get(messageId: string): Promise<TombstoneRecord | undefined> {
    const key = await idbKey(messageId);
    return get<TombstoneRecord>(key, this.idb);
  }

  // -------------------------------------------------------------------------
  // Reset (test / debug helpers)
  // -------------------------------------------------------------------------

  /** Remove the tombstone for a single messageId. Scoped to that message only. */
  async clear(messageId: string): Promise<void> {
    const key = await idbKey(messageId);
    await del(key, this.idb);
  }

  /** Wipe every tombstone in this store instance. */
  async resetAll(): Promise<void> {
    await idbClear(this.idb);
  }
}

// ---------------------------------------------------------------------------
// IDB key derivation
// ---------------------------------------------------------------------------

/**
 * Derives a stable IndexedDB key string from a messageId.
 *
 * Uses SHA-256 (Web Crypto) so the key is a fixed 64-hex string regardless
 * of messageId length or content — mirrors iOS `SHA256.hash(data:)`.
 *
 * The "tomb:" prefix distinguishes tombstone keys from other stores that
 * might share the same IndexedDB database (defensive hygiene).
 */
async function idbKey(messageId: string): Promise<string> {
  const data = new TextEncoder().encode(messageId);
  const hashBuf = await crypto.subtle.digest("SHA-256", data);
  const hex = Array.from(new Uint8Array(hashBuf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
  return `tomb:${hex}`;
}
